import { ReactNode, useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Icon } from "@iconify/react";
import { NotificationType, OnboardingFlowType } from "@markit/common.types";
import { Colors } from "../../utils/colors";
import {
  toggleOnboardingFlow,
  activateOnboardingFlow,
  getAccountState,
} from "../../redux/slices/accountSlice";
import StandardBorderedContainer from "../Containers/StandardBorderedContainer";
import { HorizontalDivider } from "../Dividers/HorizontalDivider";
import RectangleButton from "../Buttons/RectangleButton";
import { useNavigate } from "../../hooks/useNavigate";
import { NavigationId } from "../../navigation/AppParamList";
import { onCreatorPanelNavigatePath } from "../../utils/navigationUtils";
import { getCustomizeExperienceGoalTitle } from "../../utils/onboardingFlowUtils";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { CreatorSettingsTab } from "../../screens/Profile/CreatorPanels/CreatorSettingsPanel";

type TutorialManagerItem = {
  flowType: OnboardingFlowType;
  title: string;
  icon: string;
  navigateOnPress: () => void;
};

enum TutorialManagerItemState {
  GET_STARTED = "Get Started",
  RESTART = "Restart",
  ACTIVATE = "Activate",
}

// The four possible states of the checklist as a whole, driving the header content.
// - ALL_ACTIVATE: no flow has ever been touched (existing user predating
//   this checklist, or a brand new account before anything is selected).
// - ALL_FULLY_DONE: every flow has been completed at least once.
// - ALL_DONE: nothing left to start, but some flows have never been
//   activated (a mix of Restart and Activate, no Get Started).
// - IN_PROGRESS: the normal case - at least one flow is Get Started.
enum TutorialManagerHeaderState {
  ALL_ACTIVATE = "all_activate",
  ALL_FULLY_DONE = "all_fully_done",
  ALL_DONE = "all_done",
  IN_PROGRESS = "in_progress",
}

const HEADER_CONTENT: Record<
  TutorialManagerHeaderState,
  { icon: ReactNode; content: ReactNode }
> = {
  [TutorialManagerHeaderState.ALL_ACTIVATE]: {
    icon: <span style={{ fontSize: 20, flexShrink: 0 }}>👋</span>,
    content: (
      <span className="bodyMedium">
        Activate any tutorial below to see what Markit can do.
      </span>
    ),
  },
  [TutorialManagerHeaderState.ALL_FULLY_DONE]: {
    icon: <span style={{ fontSize: 20, flexShrink: 0 }}>🥳</span>,
    content: (
      <span className="bodyMedium">
        You&apos;re a Markit expert now! Restart any flow if you want a
        refresher.
      </span>
    ),
  },
  [TutorialManagerHeaderState.ALL_DONE]: {
    icon: <span style={{ fontSize: 20, flexShrink: 0 }}>🥳</span>,
    content: (
      <span className="bodyMedium">
        Nice work! Activate any remaining tutorials to see what else Markit can
        do.
      </span>
    ),
  },
  [TutorialManagerHeaderState.IN_PROGRESS]: {
    icon: <Icon icon="ion:cog" height={24} style={{ color: Colors.BLACK }} />,
    content: (
      <div className="ColumnNormal" style={{ gap: 4 }}>
        <span className="bodyMedium">Tutorial Manager</span>
        <span className="smallBodySubtext">
          View onboarding tutorials for the following features.
        </span>
      </div>
    ),
  },
};

const NUM_COLLAPSED_ITEMS = 3;

const TutorialManager = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { accountData, onboardingFlows } = useSelector(getAccountState).account;
  const [isLoadingIndex, setIsLoadingIndex] = useState<number | null>(null);
  const [showAll, setShowAll] = useState(false);

  const styles = {
    buttonContainer: {
      whiteSpace: "nowrap",
      border: `0.5px solid ${Colors.GRAY2}`,
      borderRadius: 100,
      paddingBlock: 10,
      paddingInline: 20,
      width: "fit-content",
    },
  };

  const TUTORIAL_MANAGER_ITEMS = useMemo(() => {
    const items: TutorialManagerItem[] = [
      {
        flowType: OnboardingFlowType.MCP_API,
        title: "Text from your AI agent",
        icon: "ion:sparkles",
        navigateOnPress: () =>
          onCreatorPanelNavigatePath(
            navigate,
            NavigationId.HOME_SETTINGS,
            CreatorSettingsTab.MCP
          ),
      },
    ];
    if (!accountData.profilePicURL) {
      items.push({
        flowType: OnboardingFlowType.PROFILE_PIC,
        title: "Add a profile picture",
        icon: "ion:person",
        navigateOnPress: () => navigate(NavigationId.HOME_PROFILE),
      });
    }
    items.push(
      ...[
        {
          flowType: OnboardingFlowType.INSTAGRAM_DM,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.INSTAGRAM_DM
          )!,
          icon: "mdi:instagram",
          navigateOnPress: () => navigate(NavigationId.HOME_AUTO_DM),
        },
        {
          flowType: OnboardingFlowType.COLLECT_INFO,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.COLLECT_INFO
          )!,
          icon: "ion:document-text",
          navigateOnPress: () => navigate(NavigationId.NEW_PRODUCT),
        },
        {
          flowType: OnboardingFlowType.IMPORT_CONTACTS,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.IMPORT_CONTACTS
          )!,
          icon: "ion:cloud-upload",
          navigateOnPress: () => navigate(NavigationId.HOME_IMPORT),
        },
        {
          flowType: OnboardingFlowType.NEWSLETTER_BLOG,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.NEWSLETTER_BLOG
          )!,
          icon: "ion:newspaper",
          navigateOnPress: () => navigate(NavigationId.HOME_PRODUCTS),
        },
        {
          flowType: OnboardingFlowType.SELL_PRODUCTS,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.SELL_PRODUCTS
          )!,
          icon: "ion:bag-handle",
          navigateOnPress: () => navigate(NavigationId.HOME_PRODUCTS),
        },
        {
          flowType: OnboardingFlowType.ORGANIZE_EVENTS,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.ORGANIZE_EVENTS
          )!,
          icon: "ion:calendar",
          navigateOnPress: () => navigate(NavigationId.HOME_PRODUCTS),
        },
        {
          flowType: OnboardingFlowType.PAID_MEMBERSHIP,
          title: getCustomizeExperienceGoalTitle(
            OnboardingFlowType.PAID_MEMBERSHIP
          )!,
          icon: "ion:ribbon",
          navigateOnPress: () =>
            onCreatorPanelNavigatePath(
              navigate,
              NavigationId.HOME_MEMBERSHIPS,
              2
            ),
        },
        {
          flowType: OnboardingFlowType.SEND_FIRST_TEXT,
          title: "Send your first text",
          icon: "ion:paper-plane",
          navigateOnPress: () => navigate(NavigationId.HOME_TEXTING),
        },
      ]
    );
    return items;
  }, [accountData.profilePicURL, navigate]);

  const getItemState = useCallback(
    (flowType: OnboardingFlowType): TutorialManagerItemState => {
      const flow = onboardingFlows.find((f) => f.id === flowType);
      if (!flow) {
        return TutorialManagerItemState.ACTIVATE;
      }
      return flow.platforms.web.completed
        ? TutorialManagerItemState.RESTART
        : TutorialManagerItemState.GET_STARTED;
    },
    [onboardingFlows]
  );

  // Keeps each state group in the checklist's natural order, but surfaces
  // Get Started items first, then Restart, then Activate.
  const sortedItems = useMemo(() => {
    const statePriority: Record<TutorialManagerItemState, number> = {
      [TutorialManagerItemState.GET_STARTED]: 0,
      [TutorialManagerItemState.RESTART]: 1,
      [TutorialManagerItemState.ACTIVATE]: 2,
    };
    return [...TUTORIAL_MANAGER_ITEMS].sort(
      (a, b) =>
        statePriority[getItemState(a.flowType)] -
        statePriority[getItemState(b.flowType)]
    );
  }, [TUTORIAL_MANAGER_ITEMS, getItemState]);

  const headerState = useMemo(() => {
    const itemStates = TUTORIAL_MANAGER_ITEMS.map((item) =>
      getItemState(item.flowType)
    );
    const hasGetStarted = itemStates.includes(
      TutorialManagerItemState.GET_STARTED
    );
    const hasRestart = itemStates.includes(TutorialManagerItemState.RESTART);
    const hasActivate = itemStates.includes(TutorialManagerItemState.ACTIVATE);

    if (hasActivate && !hasGetStarted && !hasRestart) {
      return TutorialManagerHeaderState.ALL_ACTIVATE;
    }
    if (hasRestart && !hasGetStarted && !hasActivate) {
      return TutorialManagerHeaderState.ALL_FULLY_DONE;
    }
    if (!hasGetStarted) {
      return TutorialManagerHeaderState.ALL_DONE;
    }
    return TutorialManagerHeaderState.IN_PROGRESS;
  }, [TUTORIAL_MANAGER_ITEMS, getItemState]);

  const itemOnPress = useCallback(
    async (item: TutorialManagerItem, index: number) => {
      const state = getItemState(item.flowType);
      setIsLoadingIndex(index);
      if (state === TutorialManagerItemState.GET_STARTED) {
        item.navigateOnPress();
      } else if (state === TutorialManagerItemState.ACTIVATE) {
        await dispatch(activateOnboardingFlow(accountData.uid, item.flowType));
        showNotificationBanner(
          dispatch,
          "Activated!",
          NotificationType.AFFIRMATIVE
        );
      } else {
        await dispatch(
          toggleOnboardingFlow(accountData.uid, item.flowType, false, false)
        );
        showNotificationBanner(
          dispatch,
          "Restarted!",
          NotificationType.AFFIRMATIVE
        );
      }
      setIsLoadingIndex(null);
    },
    [accountData.uid, dispatch, getItemState]
  );

  const renderItem = useCallback(
    (item: TutorialManagerItem, index: number) => {
      const state = getItemState(item.flowType);
      return (
        <div
          key={item.flowType}
          className="AlignedRowSpaced"
          style={{
            height: 36,
            padding: 10,
            borderRadius: 12,
            border: `0.5px solid ${Colors.GRAY2}`,
          }}
        >
          <div
            className="AlignedRow"
            style={{
              gap: 10,
              opacity: state === TutorialManagerItemState.ACTIVATE ? 0.4 : 1,
            }}
          >
            <Icon
              icon={item.icon}
              height={22}
              style={{ color: Colors.BLACK, flexShrink: 0 }}
            />
            <span className="bodyMedium">{item.title}</span>
          </div>
          <RectangleButton
            buttonLabel={state}
            onPress={() => itemOnPress(item, index)}
            altColor={
              state === TutorialManagerItemState.GET_STARTED
                ? Colors.BLACK
                : Colors.WHITE1
            }
            altTextColor={
              state === TutorialManagerItemState.GET_STARTED
                ? Colors.WHITE
                : Colors.BLACK
            }
            altPaddingVert={10}
            altPaddingHorz={14}
            altBorderRadius={100}
            width={110}
            loading={isLoadingIndex === index}
            containerStyles={{
              whiteSpace: "nowrap",
              ...(state !== TutorialManagerItemState.GET_STARTED
                ? { border: `0.5px solid ${Colors.GRAY2}` }
                : {}),
            }}
          />
        </div>
      );
    },
    [getItemState, isLoadingIndex, itemOnPress]
  );

  const displayedItems = showAll
    ? sortedItems
    : sortedItems.slice(0, NUM_COLLAPSED_ITEMS);

  return (
    <StandardBorderedContainer
      containerStyles={{
        flex: 1,
        borderRadius: 20,
        paddingBlock: 14,
        backgroundColor: Colors.WHITE,
      }}
    >
      <div className="ColumnNormal" style={{ gap: 14 }}>
        <div className="AlignedRow" style={{ gap: 10, paddingInline: 14 }}>
          {HEADER_CONTENT[headerState].icon}
          {HEADER_CONTENT[headerState].content}
        </div>
        <HorizontalDivider altMargin={0} />
        <div className="ColumnNormal" style={{ paddingInline: 10, gap: 7 }}>
          {displayedItems.map((item, index) => renderItem(item, index))}
        </div>
        <div className="ColumnCentering" style={{ gap: 10, paddingInline: 10 }}>
          {showAll ? (
            <RectangleButton
              buttonLabel="Hide Tutorials"
              onPress={() => setShowAll(false)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={styles.buttonContainer}
            />
          ) : (
            <RectangleButton
              buttonLabel="View All Tutorials"
              onPress={() => setShowAll(true)}
              altColor={Colors.WHITE1}
              altTextColor={Colors.BLACK}
              containerStyles={styles.buttonContainer}
            />
          )}
        </div>
      </div>
    </StandardBorderedContainer>
  );
};

export default TutorialManager;
